import { useState, useEffect } from 'react';
import './DiceRoller.css';

const DIE_FACES = {
  d4: [1, 2, 3, 4],
  d6: [1, 2, 3, 4, 5, 6],
  d8: [1, 2, 3, 4, 5, 6, 7, 8],
  d10: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  d12: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  d20: Array.from({ length: 20 }, (_, i) => i + 1),
  d100: Array.from({ length: 10 }, (_, i) => i * 10)
};

export default function DiceRoller({ dieType, purpose, modifier = 0, dc, onRoll, onClose }) {
  const [rolling, setRolling] = useState(false);
  const [displayValue, setDisplayValue] = useState('?');
  const [finalResult, setFinalResult] = useState(null);

  const faces = DIE_FACES[dieType] || DIE_FACES.d20;
  const maxValue = dieType === 'd100' ? 100 : parseInt(dieType.slice(1));

  const rollDie = () => {
    setRolling(true);
    setFinalResult(null);

    // Animation - rapidly change displayed value
    let iterations = 0;
    const maxIterations = 20;
    const interval = setInterval(() => {
      setDisplayValue(faces[Math.floor(Math.random() * faces.length)]);
      iterations++;

      if (iterations >= maxIterations) {
        clearInterval(interval);

        // Generate final result
        let result;
        if (dieType === 'd100') {
          result = Math.floor(Math.random() * 100) + 1;
        } else if (dieType === 'd10') {
          result = Math.floor(Math.random() * 10) + 1;
        } else {
          result = Math.floor(Math.random() * maxValue) + 1;
        }

        setDisplayValue(result);
        setFinalResult(result);
        setRolling(false);
      }
    }, 50);
  };

  const confirmRoll = () => {
    if (finalResult !== null) {
      onRoll({
        dieType,
        roll: finalResult,
        modifier,
        total: finalResult + modifier
      });
    }
  };

  const getDieShape = () => {
    switch (dieType) {
      case 'd4': return 'triangle';
      case 'd6': return 'square';
      case 'd8': return 'octagon';
      case 'd10': return 'decagon';
      case 'd12': return 'dodecagon';
      case 'd20': return 'icosahedron';
      case 'd100': return 'sphere';
      default: return 'icosahedron';
    }
  };

  return (
    <div className="dice-roller-overlay">
      <div className="dice-roller-modal">
        <button className="close-btn" onClick={onClose}>&times;</button>

        <div className="roll-purpose">
          <h3>Roll Required</h3>
          <p>{purpose}</p>
          {dc && <p className="dc-display">DC: {dc}</p>}
        </div>

        <div
          className={`die-container ${rolling ? 'rolling' : ''} ${finalResult ? 'landed' : ''} ${!rolling && !finalResult ? 'clickable' : ''}`}
          onClick={!rolling && !finalResult ? rollDie : undefined}
          title={!rolling && !finalResult ? 'Click to roll!' : ''}
        >
          <div className={`die die-${getDieShape()}`}>
            <span className="die-value">{displayValue}</span>
          </div>
          <span className="die-type">{dieType.toUpperCase()}</span>
        </div>

        {modifier !== 0 && (
          <div className="modifier-display">
            Modifier: {modifier >= 0 ? '+' : ''}{modifier}
          </div>
        )}

        {finalResult !== null && (
          <div className="roll-result">
            <div className="result-breakdown">
              <span>{finalResult}</span>
              {modifier !== 0 && (
                <>
                  <span className="operator">{modifier >= 0 ? '+' : ''}</span>
                  <span>{modifier}</span>
                </>
              )}
              <span className="operator">=</span>
              <span className="total">{finalResult + modifier}</span>
            </div>
            {dc && (
              <div className={`success-indicator ${finalResult + modifier >= dc ? 'success' : 'failure'}`}>
                {finalResult + modifier >= dc ? 'SUCCESS!' : 'FAILURE'}
              </div>
            )}
          </div>
        )}

        <div className="roll-actions">
          {finalResult === null ? (
            <button
              className="btn btn-primary roll-btn"
              onClick={rollDie}
              disabled={rolling}
            >
              {rolling ? 'Rolling...' : 'Roll the Dice'}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={confirmRoll}
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
